import { useMemo } from 'react';
import { FIELD_PALETTE } from '../lib/fieldPalette';
import type { FieldType } from '../types/forms';
import type { LucideIcon } from 'lucide-react';

export interface FieldTypeCatalogItem {
  type: FieldType;
  label: string;
  icon: LucideIcon;
  category: string;
}

export interface FieldTypeCatalog {
  items: readonly FieldTypeCatalogItem[];
  byCategory: Readonly<Record<string, readonly FieldTypeCatalogItem[]>>;
  categoryLabels: Readonly<Record<string, string>>;
  columns: readonly (readonly string[])[];
}

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  contact_info: 'Informações de Contato',
  text_media: 'Texto & Mídia',
  choice: 'Escolha',
  rating_ranking: 'Avaliação & Ranking',
  other: 'Outros',
  layout: 'Layout',
};

// Column layout per Story 4.1 — 3 columns, 2 categories each.
const COLUMNS: readonly (readonly string[])[] = [
  ['contact_info', 'text_media'],
  ['choice', 'other'],
  ['rating_ranking', 'layout'],
];

/**
 * Hook que expõe o catálogo de field types pronto pra UI:
 * lista flat + agrupamento por categoria + layout de colunas + rótulos pt-BR.
 *
 * Share entre AddFieldModal desktop + add-field UI mobile (Phase 3).
 * Memoized na primeira chamada — FIELD_PALETTE é constante de build.
 */
export const useFieldTypeCatalog = (): FieldTypeCatalog => {
  return useMemo(() => {
    const byCategory = FIELD_PALETTE.reduce<Record<string, FieldTypeCatalogItem[]>>(
      (acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
      },
      {},
    );
    return {
      items: FIELD_PALETTE as readonly FieldTypeCatalogItem[],
      byCategory,
      categoryLabels: CATEGORY_LABELS,
      columns: COLUMNS,
    };
  }, []);
};
